/**
 * ScheduleContext — backward-compatible façade that composes
 * NavigationContext (view / category / subcategory) and
 * BookingContext (everything else).
 *
 * New code should import `useNavigation` or `useBooking` directly
 * to avoid unnecessary re-renders. This file exists so existing
 * consumers can migrate incrementally.
 */
import { useMemo, useCallback, type ReactNode } from "react";
import { NavigationProvider, useNavigation } from "@/contexts/NavigationContext";
import { BookingProvider, useBooking, type BookingState } from "@/contexts/BookingContext";
import { EVENT_CATEGORIES, DURATIONS } from "@/data/categories";
import type { ViewName, Duration, TimeSlot, OrgUser, NlpResult, TeamMember } from "@/types";

// ─── Combined state (for backward compat) ───
export interface ScheduleState {
  view: ViewName;
  category: string | null;
  subcategory: string | null;
  sel: string[];
  dur: Duration | null;
  tentFree: boolean;
  search: string;
  chosen: TimeSlot | null;
  title: string;
  candidate: string;
  candidateEmail: string;
  position: string;
  notes: string;
  videoPlatform: string;
  customVideoLink: string;
  sentEmails: string[];
  copied: boolean;
  syncing: boolean;
  previewTab: string;
  previewPerson: TeamMember | null;
  interviewerTpl: string;
  candidateTpl: string;
  orgUsers: OrgUser[];
  selectedMembers: string[];
  duration: Duration | null;
}

// ─── Combined action type (for backward compat) ───
export type ScheduleAction =
  | { type: "SET_VIEW"; view: ViewName }
  | { type: "SELECT_CATEGORY"; category: string | null }
  | { type: "SELECT_SUBCATEGORY"; subcategory: string | null; defaultDur?: number; ids?: string[] }
  | { type: "TOGGLE_MEMBER"; id: string }
  | { type: "SET_MEMBERS"; ids: string[] }
  | { type: "SET_DURATION"; dur: Duration }
  | { type: "SET_TENT_FREE"; value: boolean }
  | { type: "SET_SEARCH"; value: string }
  | { type: "BOOK_SLOT"; slot: TimeSlot }
  | { type: "SET_FIELD"; field: keyof ScheduleState; value: unknown }
  | { type: "CONFIRM_BOOKING"; emails: string[] }
  | { type: "FINISH_SYNC" }
  | { type: "NLP_ACTION"; result: NlpResult }
  | { type: "SET_ORG_USERS"; users: OrgUser[] }
  | { type: "RESET" };

// ─── Composite Provider ───
function BookingBridge({ children }: { children: ReactNode }) {
  const { nav } = useNavigation();
  return <BookingProvider category={nav.category}>{children}</BookingProvider>;
}

export function ScheduleProvider({ children }: { children: ReactNode }) {
  return (
    <NavigationProvider>
      <BookingBridge>{children}</BookingBridge>
    </NavigationProvider>
  );
}

// ─── Backward-compatible hook ───
export function useSchedule() {
  const { nav, navDispatch } = useNavigation();
  const { booking, bookingDispatch, isInterview } = useBooking();

  // Merge into combined state
  const state: ScheduleState = useMemo(
    () => ({
      ...booking,
      view: nav.view,
      category: nav.category,
      subcategory: nav.subcategory,
      selectedMembers: booking.sel,
      duration: booking.dur,
    }),
    [nav, booking]
  );

  // Unified dispatch that routes actions to the correct reducer
  const dispatch = useCallback(
    (action: ScheduleAction) => {
      switch (action.type) {
        // ── Navigation-only actions ──
        case "SET_VIEW":
          navDispatch(action);
          break;

        case "SELECT_CATEGORY":
          navDispatch(action);
          break;

        // ── Cross-cutting: nav + booking ──
        case "SELECT_SUBCATEGORY":
          navDispatch({ type: "SELECT_SUBCATEGORY", subcategory: action.subcategory });
          if (action.subcategory) {
            bookingDispatch({
              type: "APPLY_SUBCATEGORY_DEFAULTS",
              defaultDur: action.defaultDur,
              ids: action.ids,
            });
          }
          break;

        case "BOOK_SLOT":
          bookingDispatch({ type: "BOOK_SLOT", slot: action.slot });
          navDispatch({ type: "SET_VIEW", view: "confirm" });
          break;

        case "FINISH_SYNC":
          bookingDispatch({ type: "FINISH_SYNC" });
          navDispatch({ type: "SET_VIEW", view: "done" });
          break;

        case "NLP_ACTION": {
          const r = action.result;
          const updates: Partial<BookingState> = {};
          if (r.duration) updates.dur = DURATIONS.find((d) => d.mins === r.duration) || DURATIONS[1];
          if (r.people?.length) updates.sel = r.people;
          if (r.candidate) updates.candidate = r.candidate;
          if (r.position) updates.position = r.position;
          if (r.platform) updates.videoPlatform = r.platform;
          bookingDispatch({ type: "APPLY_NLP", updates });

          // Navigation side-effects
          navDispatch({ type: "SET_VIEW", view: "pick" });
          if (r.category) navDispatch({ type: "SELECT_CATEGORY", category: r.category });
          if (r.subcategory) {
            navDispatch({ type: "SELECT_SUBCATEGORY", subcategory: r.subcategory });
          } else if (r.category) {
            const cat = EVENT_CATEGORIES.find((c) => c.id === r.category);
            if (cat?.subcategories?.length) {
              navDispatch({ type: "SELECT_SUBCATEGORY", subcategory: cat.subcategories[0].id });
            }
          }
          break;
        }

        case "CONFIRM_BOOKING":
          bookingDispatch(action);
          break;

        case "SET_ORG_USERS":
          bookingDispatch(action);
          break;

        // ── Booking-only actions ──
        case "TOGGLE_MEMBER":
        case "SET_MEMBERS":
        case "SET_DURATION":
        case "SET_TENT_FREE":
        case "SET_SEARCH":
          bookingDispatch(action);
          break;

        case "SET_FIELD": {
          // Route to correct context based on field name
          const navFields: Set<string> = new Set(["view", "category", "subcategory"]);
          if (navFields.has(action.field)) {
            if (action.field === "view") navDispatch({ type: "SET_VIEW", view: action.value as ViewName });
          } else {
            bookingDispatch({ type: "SET_FIELD", field: action.field as keyof BookingState, value: action.value });
          }
          break;
        }

        case "RESET":
          navDispatch({ type: "NAV_RESET" });
          bookingDispatch({ type: "BOOKING_RESET" });
          break;
      }
    },
    [navDispatch, bookingDispatch]
  );

  return { state, dispatch, isInterview };
}
